import React, { useEffect, useState } from 'react';
import { Box, Card, CardContent, Grid, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Divider, Pagination } from '@mui/material';
import { styled } from '@mui/system'
import "./rewardAnalysis.css"
import { useNavigate } from 'react-router-dom'
import { toast, ToastContainer } from 'react-toastify'
import Loader from '../../components/Loader/Loader'
import Toptag from '../../components/toptag/Toptag'
import apiFunctions from '../../api/apiFunctions'
import { TrendingUp, AttachMoney, CheckCircle, Pending, Analytics, People, Schedule } from '@mui/icons-material'

const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: "24px",
    borderRadius: "12px",
    backgroundColor: "white",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    transition: "box-shadow 0.3s ease",
    '&:hover': {
        boxShadow: "0 4px 12px rgba(0,0,0,0.12)"
    }
}))

const StatCard = styled(Card)(({ theme, color }) => ({
    padding: "24px",
    borderRadius: "12px",
    backgroundColor: "white",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    textAlign: "center",
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
    borderLeft: `4px solid ${color || "#3b82f6"}`,
    '&:hover': {
        transform: "translateY(-4px)",
        boxShadow: "0 8px 16px rgba(0,0,0,0.15)"
    }
}))

const InfoCard = styled(Card)(({ theme }) => ({
    padding: "20px",
    borderRadius: "12px",
    backgroundColor: "#f8f9fa",
    border: "1px solid #e5e7eb",
    transition: "all 0.2s ease",
    '&:hover': {
        backgroundColor: "#f1f3f5",
        borderColor: "#d1d5db"
    }
}))

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    backgroundColor: "white",
    marginTop: "16px",
    '& .MuiTableHead-root': {
        backgroundColor: "#f5f7fa",
        '& .MuiTableCell-head': {
            fontWeight: 600,
            fontSize: "14px",
            color: "#374151",
            borderBottom: "2px solid #e5e7eb"
        }
    },
    '& .MuiTableBody-root': {
        '& .MuiTableRow-root': {
            transition: "background-color 0.2s ease",
            '&:hover': {
                backgroundColor: "#f9fafb"
            },
            '&:nth-of-type(even)': {
                backgroundColor: "#fafbfc"
            }
        },
        '& .MuiTableCell-root': {
            fontSize: "14px",
            color: "#4b5563",
            borderBottom: "1px solid #e5e7eb",
            padding: "16px"
        }
    }
}))

export default function RewardAnalysis() {
    const [analysis, setAnalysis] = useState(null);
    const [isLoading, setIsloading] = useState(true)
    const navigate = useNavigate();
    const [rewardsByMonthPage, setRewardsByMonthPage] = useState(1);
    const [rewardsByMonthLimit] = useState(12);

    const getAnalysis = async () => {
        setIsloading(true);
        try {
            let result = await apiFunctions.getRewardAnalysis(rewardsByMonthPage, rewardsByMonthLimit);
            if (!result.data.status) {
                if (result.data.code === 401) {
                    localStorage.clear();
                    navigate("/login")
                    toast.info("Session Expired Please login again!!")
                }
                toast.error(result.data.message, {
                    position: toast.POSITION.TOP_RIGHT
                });
                setIsloading(false);
            } else {
                setAnalysis(result.data.data);
                setIsloading(false)
            }
        } catch (error) {
            toast.error("Error loading reward analysis", {
                position: toast.POSITION.TOP_RIGHT
            });
            setIsloading(false);
        }
    }

    useEffect(() => {
        getAnalysis();
    }, [rewardsByMonthPage])

    if (isLoading) {
        return (
            <Box sx={{ padding: "20px", backgroundColor: "#f5f7fa", minHeight: "100vh" }}>
                <Toptag name="Reward Analysis" />
                <Box sx={{ display: "flex", justifyContent: "center", padding: 4 }}>
                    <Loader />
                </Box>
            </Box>
        )
    }

    if (!analysis) {
        return (
            <Box sx={{ padding: "20px", backgroundColor: "#f5f7fa", minHeight: "100vh" }}>
                <Toptag name="Reward Analysis" />
                <StyledPaper style={{ marginTop: "20px", textAlign: "center" }}>
                    <Typography variant="h6" color="textSecondary">No analysis data available</Typography>
                </StyledPaper>
            </Box>
        )
    }

    const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

    return (
        <Box sx={{ padding: "20px", backgroundColor: "#f5f7fa", minHeight: "100vh" }}>
            <Toptag name="Reward Analysis & Overview" />
            <Grid container spacing={3} style={{ marginTop: "10px" }}>
                <Grid item xs={12}>
                    <StyledPaper>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, marginBottom: 3 }}>
                            <Analytics sx={{ fontSize: 32, color: "#3b82f6" }} />
                            <Typography variant="h5" sx={{ color: "#1f2937", fontWeight: 600 }}>
                                Active Reward Configuration
                            </Typography>
                        </Box>
                        {analysis.activeRewardConfig ? (
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <InfoCard>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, marginBottom: 1 }}>
                                            <People sx={{ color: "#3b82f6" }} />
                                            <Typography variant="subtitle2" color="textSecondary">Minimum Users Required</Typography>
                                        </Box>
                                        <Typography variant="h4" sx={{ color: "#3b82f6", fontWeight: 600 }}>
                                            {analysis.activeRewardConfig.minimum_users_required}
                                        </Typography>
                                    </InfoCard>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <InfoCard>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, marginBottom: 1 }}>
                                            <AttachMoney sx={{ color: "#10b981" }} />
                                            <Typography variant="subtitle2" color="textSecondary">
                                                Amount Per Hour ({analysis.activeRewardConfig.currency || 'USDC'})
                                            </Typography>
                                        </Box>
                                        <Typography variant="h4" sx={{ color: "#10b981", fontWeight: 600 }}>
                                            {analysis.activeRewardConfig.amount_per_hour}
                                        </Typography>
                                    </InfoCard>
                                </Grid>
                            </Grid>
                        ) : (
                            <Typography color="textSecondary">No active reward configuration</Typography>
                        )}
                    </StyledPaper>
                </Grid>

                <Grid item xs={12} md={6}>
                    <StyledPaper>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, marginBottom: 2 }}>
                            <Schedule sx={{ fontSize: 24, color: "#3b82f6" }} />
                            <Typography variant="h6" sx={{ color: "#1f2937", fontWeight: 600 }}>
                                Slot Rewards Summary
                            </Typography>
                        </Box>
                        <Divider sx={{ marginBottom: 2 }} />
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: 2, backgroundColor: "#f8f9fa", borderRadius: "8px", marginBottom: 1 }}>
                                    <Typography variant="body1" sx={{ fontWeight: 500 }}>Total Slot Rewards</Typography>
                                    <Typography variant="h6" sx={{ color: "#3b82f6", fontWeight: 600 }}>
                                        {analysis.slotRewards?.total || 0}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={6}>
                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: 2, backgroundColor: "#ecfdf5", borderRadius: "8px" }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <CheckCircle sx={{ color: "#10b981", fontSize: 20 }} />
                                        <Typography variant="body2" color="textSecondary">Finalized</Typography>
                                    </Box>
                                    <Typography variant="h6" sx={{ color: "#10b981", fontWeight: 600 }}>
                                        {analysis.slotRewards?.finalized || 0}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={6}>
                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: 2, backgroundColor: "#fef3c7", borderRadius: "8px" }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <Pending sx={{ color: "#f59e0b", fontSize: 20 }} />
                                        <Typography variant="body2" color="textSecondary">Pending</Typography>
                                    </Box>
                                    <Typography variant="h6" sx={{ color: "#f59e0b", fontWeight: 600 }}>
                                        {analysis.slotRewards?.pending || 0}
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    </StyledPaper>
                </Grid>

                <Grid item xs={12} md={6}>
                    <StyledPaper>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, marginBottom: 2 }}>
                            <AttachMoney sx={{ fontSize: 24, color: "#10b981" }} />
                            <Typography variant="h6" sx={{ color: "#1f2937", fontWeight: 600 }}>
                                Monthly Payments Summary
                            </Typography>
                        </Box>
                        <Divider sx={{ marginBottom: 2 }} />
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: 2, backgroundColor: "#f8f9fa", borderRadius: "8px", marginBottom: 1 }}>
                                    <Typography variant="body1" sx={{ fontWeight: 500 }}>Total Monthly Payments</Typography>
                                    <Typography variant="h6" sx={{ color: "#3b82f6", fontWeight: 600 }}>
                                        {analysis.monthlyPayments?.total || 0}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={4}>
                                <Box sx={{ textAlign: "center", padding: 2, backgroundColor: "#fef3c7", borderRadius: "8px" }}>
                                    <Typography variant="h5" sx={{ color: "#f59e0b", fontWeight: 600, marginBottom: 0.5 }}>
                                        {analysis.monthlyPayments?.pending || 0}
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary">Pending</Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={4}>
                                <Box sx={{ textAlign: "center", padding: 2, backgroundColor: "#dbeafe", borderRadius: "8px" }}>
                                    <Typography variant="h5" sx={{ color: "#3b82f6", fontWeight: 600, marginBottom: 0.5 }}>
                                        {analysis.monthlyPayments?.approved || 0}
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary">Approved</Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={4}>
                                <Box sx={{ textAlign: "center", padding: 2, backgroundColor: "#d1fae5", borderRadius: "8px" }}>
                                    <Typography variant="h5" sx={{ color: "#10b981", fontWeight: 600, marginBottom: 0.5 }}>
                                        {analysis.monthlyPayments?.paid || 0}
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary">Paid</Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    </StyledPaper>
                </Grid>

                <Grid item xs={12}>
                    <StyledPaper>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, marginBottom: 2 }}>
                            <TrendingUp sx={{ fontSize: 28, color: "#8b5cf6" }} />
                            <Typography variant="h6" sx={{ color: "#1f2937", fontWeight: 600 }}>
                                Earnings Summary
                            </Typography>
                        </Box>
                        <Divider sx={{ marginBottom: 3 }} />
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={4}>
                                <StatCard color="#3b82f6">
                                    <AttachMoney sx={{ fontSize: 40, color: "#3b82f6", marginBottom: 2 }} />
                                    <Typography variant="h3" sx={{ color: "#3b82f6", fontWeight: 700, marginBottom: 1 }}>
                                        {analysis.earningsSummary?.totalEarned?.toFixed(2) || '0.00'}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 500 }}>
                                        Total Earned
                                    </Typography>
                                </StatCard>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <StatCard color="#10b981">
                                    <CheckCircle sx={{ fontSize: 40, color: "#10b981", marginBottom: 2 }} />
                                    <Typography variant="h3" sx={{ color: "#10b981", fontWeight: 700, marginBottom: 1 }}>
                                        {analysis.earningsSummary?.totalPaid?.toFixed(2) || '0.00'}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 500 }}>
                                        Total Paid
                                    </Typography>
                                </StatCard>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <StatCard color="#f59e0b">
                                    <Pending sx={{ fontSize: 40, color: "#f59e0b", marginBottom: 2 }} />
                                    <Typography variant="h3" sx={{ color: "#f59e0b", fontWeight: 700, marginBottom: 1 }}>
                                        {analysis.earningsSummary?.totalPending?.toFixed(2) || '0.00'}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 500 }}>
                                        Total Pending
                                    </Typography>
                                </StatCard>
                            </Grid>
                        </Grid>
                    </StyledPaper>
                </Grid>

                {analysis.rewardsByMonth && analysis.rewardsByMonth.data && analysis.rewardsByMonth.data.length > 0 && (
                    <Grid item xs={12}>
                        <StyledPaper>
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 2 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                    <TrendingUp sx={{ fontSize: 24, color: "#3b82f6" }} />
                                    <Typography variant="h6" sx={{ color: "#1f2937", fontWeight: 600 }}>
                                        Rewards by Month
                                    </Typography>
                                </Box>
                                <Typography variant="body2" color="textSecondary">
                                    Total: {analysis.rewardsByMonth.total || 0} months
                                </Typography>
                            </Box>
                            <Divider sx={{ marginBottom: 2 }} />
                            <StyledTableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Year</TableCell>
                                            <TableCell>Month</TableCell>
                                            <TableCell align="center">Number of Rewards</TableCell>
                                            <TableCell>Currency</TableCell>
                                            <TableCell align="right">Total Amount</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {analysis.rewardsByMonth.data.map((item, index) => (
                                            <TableRow key={index}>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                        {item._id.year}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {monthNames[item._id.month - 1]}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Typography variant="body2" sx={{ fontWeight: 600, color: "#3b82f6" }}>
                                                        {item.count}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {item.currency || 'USDC'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Typography variant="body2" sx={{ fontWeight: 600, color: "#10b981" }}>
                                                        {item.total_amount?.toFixed(2) || '0.00'}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </StyledTableContainer>
                            {analysis.rewardsByMonth.totalPages > 1 && (
                                <Box sx={{ display: "flex", justifyContent: "center", marginTop: 3, paddingTop: 2, borderTop: "1px solid #e5e7eb" }}>
                                    <Pagination
                                        count={analysis.rewardsByMonth.totalPages}
                                        page={rewardsByMonthPage}
                                        onChange={(e, newPage) => setRewardsByMonthPage(newPage)}
                                        color="primary"
                                        shape="rounded"
                                    />
                                </Box>
                            )}
                        </StyledPaper>
                    </Grid>
                )}

                {analysis.topEarningUsers && analysis.topEarningUsers.length > 0 && (
                    <Grid item xs={12}>
                        <StyledPaper>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2, marginBottom: 2 }}>
                                <People sx={{ fontSize: 24, color: "#8b5cf6" }} />
                                <Typography variant="h6" sx={{ color: "#1f2937", fontWeight: 600 }}>
                                    Top Earning Users
                                </Typography>
                            </Box>
                            <Divider sx={{ marginBottom: 2 }} />
                            <StyledTableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Rank</TableCell>
                                            <TableCell>User Name</TableCell>
                                            <TableCell>Email</TableCell>
                                            <TableCell align="right">Total Amount</TableCell>
                                            <TableCell align="center">Payments</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {analysis.topEarningUsers.map((user, index) => (
                                            <TableRow key={index}>
                                                <TableCell>
                                                    <Box sx={{
                                                        display: "inline-flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        width: 32,
                                                        height: 32,
                                                        borderRadius: "50%",
                                                        backgroundColor: index < 3 ? "#3b82f6" : "#e5e7eb",
                                                        color: index < 3 ? "white" : "#6b7280",
                                                        fontWeight: 600,
                                                        fontSize: "14px"
                                                    }}>
                                                        {index + 1}
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                        {user.user_name || 'N/A'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ color: "#6b7280" }}>
                                                        {user.email || 'N/A'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Typography variant="body2" sx={{ fontWeight: 600, color: "#10b981" }}>
                                                        {user.total_amount?.toFixed(2) || '0.00'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Typography variant="body2" sx={{ fontWeight: 500, color: "#3b82f6" }}>
                                                        {user.paymentCount || 0}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </StyledTableContainer>
                        </StyledPaper>
                    </Grid>
                )}
            </Grid>
            <ToastContainer />
        </Box>
    )
}
